import type { CarrierOperator } from "@/api/carrier";
import type { SocketConnectionState } from "@/services/socket/socket-context";

export type OperatorLocationFreshness = "fresh" | "stale" | "offline";
export type DispatchFreshness =
  | "connected"
  | "stale"
  | "reconnecting"
  | "disconnected";

const FRESH_LOCATION_MS = 2 * 60 * 1000;
const OFFLINE_LOCATION_MS = 5 * 60 * 1000;
const STALE_DISPATCH_MS = 2 * 60 * 1000;

export function operatorLocationFreshness(
  operator: Pick<CarrierOperator, "status" | "isTracking" | "lastLocationTime">,
  now = Date.now()
): OperatorLocationFreshness {
  if (
    operator.status === "OFF_DUTY" ||
    operator.isTracking === false ||
    !operator.lastLocationTime
  ) {
    return "offline";
  }
  const timestamp = Date.parse(operator.lastLocationTime);
  if (!Number.isFinite(timestamp) || now - timestamp > OFFLINE_LOCATION_MS) {
    return "offline";
  }
  return now - timestamp <= FRESH_LOCATION_MS ? "fresh" : "stale";
}

export function dispatchFreshness({
  connectionState,
  lastDataRefreshAt,
  lastDisconnectedAt,
  now = Date.now(),
}: {
  connectionState: SocketConnectionState;
  lastDataRefreshAt: number;
  lastDisconnectedAt: number | null;
  now?: number;
}): DispatchFreshness {
  if (connectionState !== "connected") return connectionState;
  if (
    lastDataRefreshAt <= 0 ||
    (lastDisconnectedAt !== null && lastDataRefreshAt <= lastDisconnectedAt) ||
    now - lastDataRefreshAt > STALE_DISPATCH_MS
  ) {
    return "stale";
  }
  return "connected";
}
